import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import Sidebar from '../../Sidebar';
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { getAllDesignMix, deleteDesignMix, printDesignMix } from "../../services/DesignMixServices";
import { displayError } from "../../helpers";
import { openPdfResponse } from "../../utils/openPdfInApp";
import { buildDesignMixQueryParams } from "../../utils/listViewDateRanges";
import { getDefaultListViewConfig } from "../../config/listViewSettingsConfig";
import { bootstrapListViewLoad } from "../../utils/listViewBootstrap";
import ListFormPageHeader from "../../components/ListFormPageHeader";
import ListPageGradientSummary from "../../components/ListPageGradientSummary";
import { buildGradientCard, formatQty, sumField } from "../../utils/listSummaryUtils";
import moment from 'moment';

function DesignMixTable() {
    const InitialData = {
					   
        records: []
      };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate(); 
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true); 
    const right_pinned = ["id"];
    const todayDateLabel = moment().format('DD-MM-YYYY');

    const highlightCard = useMemo(() => {
        const todayCount = data.records.filter((row) => row.work_schedule_date === todayDateLabel).length;
        return buildGradientCard('Today Mix', todayCount, 'today');
    }, [data.records, todayDateLabel]);

    const summaryCards = useMemo(() => [
        buildGradientCard('Total Mix', data.records.length, 'blue'),
        buildGradientCard('Sch Qty', formatQty(sumField(data.records, 'scheduled_quantity')), 'purple'),
        buildGradientCard('Order Qty', formatQty(sumField(data.records, 'ordered_quantity')), 'teal'),
    ], [data.records]);

    const columns = useMemo(
        () => [
            {
                fieldName: 'plant', 
                headerName: 'Plant'
            },
            {
                fieldName: 'sales_order_no', 
                headerName: 'SO No'
            },
            {
                fieldName: "sales_order_date",
                headerName: 'SO Date'
            },
            {
                fieldName: 'work_schedule_no', 
                headerName: 'Work schedule No'
            },
            {
                fieldName: "work_schedule_date",
                headerName: 'Work schedule Date'
            },
            {
                fieldName: "customer_id",
                headerName: 'Customer'
            },
            {
                fieldName: "consignee_id",
                headerName: 'Site / Project'
            },
            {
                fieldName: "grade",
                headerName: 'Grade'
            },
            {
                fieldName: 'recipe_code',
                headerName: 'Recipe Code'
            },
            {
                fieldName: "rate",
                headerName: 'Rate'
            },
            {
                fieldName: "ordered_quantity",
                headerName: 'Order Qty'
            },
            {
                fieldName: "scheduled_quantity",
                headerName: 'Scheduled Qty'
            },
            {
                fieldName: "delivery_mode",
                headerName: 'Delivery Mode'
            },
            {
                fieldName: "concrete_structure_id",
                headerName: 'Concrete Structure'
            },
            {
                fieldName: "id",
                headerName: "ROW_ACTIONS"
            }
        ],
        [],);

    const onEditDesignMix = (designmixId, event) => {
       
       navigate(`/DesignMixEdit/${designmixId}/`);
    };

    const onDeleteDesignMix = (designmixId, event) => {

        event.preventDefault();
        Swal.fire({title: 'Are you sure to Delete?',  
        showCancelButton: true,  
        confirmButtonText: `Yes`,  
        cancelButtonText: `No`,
        }).then((result) => {  
          if (result.isConfirmed) { 
            setIsLoading(true);
            deleteDesignMix(cookies, designmixId)
            .then(() => {
                Swal.fire('Deleted Successfully!', '', 'success');
                setRefreshKey(oldKey => oldKey + 1);
            })
            .catch((error) => {
                setIsLoading(false);
                displayError(error.response?.data ?? error.message, 'Delete Failed');
            });
          } else if (result.isDismissed) {    
            Swal.fire('Not Deleted', '', 'info')  
        }
        });
    };

    const onPrintDesignMix = (designmixId, event) => {
        event.preventDefault();
        setIsLoading(true);
        printDesignMix(cookies, designmixId)
            .then((response) => {
                openPdfResponse(response, { title: 'Design Mix', downloadFileName: 'design_mix.pdf' });
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                displayError(error.response?.data ?? error.message, 'Print Failed');
            });
    };

    const handleCSVExport = (event) => {
        console.log('CSV Export');
        exportAsCSV(
            columns
            .filter(col => col.headerName !== 'ROW_ACTIONS')
            .map(c => c.headerName),
            data.records.map(r => {

                const csvRow = {
                    plant:r.plant,
                    sales_order_no: r.sales_order_no,
                    sales_order_date: r.sales_order_date,
                    customer_id:r.customer_id,
                    consignee_id: r.consignee_id,
                    grade: r.grade,
                    recipe_code: r.recipe_code,
                    rate:r.rate,
                    ordered_quantity: r.ordered_quantity,
                    delivery_mode:r.delivery_mode,
                    concrete_structure_id: r.concrete_structure_id,
                    work_schedule_no :r.work_schedule_no,
                    work_schedule_date:r.work_schedule_date,
                    scheduled_quantity:r.scheduled_quantity
                }
                return csvRow;
                }));
    };

    const handlePDFExport = (event) => {
        console.log('PDF Export');
        exportAsPDF(
            'LIST OF DESIGN MIX',
            columns
            .filter(col => col.headerName !== 'ROW_ACTIONS')
            .map(col=>({header: col.headerName, dataKey: col.fieldName })),
            data.records.map(r => ({
                plant:r.plant,
                sales_order_no: r.sales_order_no,
                sales_order_date: r.sales_order_date,
                customer_id:r.customer_id,
                consignee_id: r.consignee_id,
                grade: r.grade,
                recipe_code: r.recipe_code,
                rate:r.rate,
                ordered_quantity: r.ordered_quantity,
                delivery_mode:r.delivery_mode,
                concrete_structure_id: r.concrete_structure_id,
                work_schedule_no :r.work_schedule_no,
                work_schedule_date:r.work_schedule_date,
                scheduled_quantity:r.scheduled_quantity
            })),
            'DesignMixList.pdf'
        );
    };

    const loadDesignMixList = useCallback((config) => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return Promise.resolve();
        }
        const activeConfig = config || getDefaultListViewConfig('design_mix');
        setIsLoading(true);
        return getAllDesignMix(cookies, {
            ...buildDesignMixQueryParams(activeConfig),
            limit: 500,
        })
        .then( designmixList => {
            const tableData = (designmixList?.design_mix_list || [])
            .map(r => {
                const salesOrder = r.work_schedule?.sales_order_detail?.sales_order;
                const prefix = salesOrder?.prefix ?? '';
                const orderNo = salesOrder?.order_no ?? '';
                return {
                    id: r.id,
                    sales_order_no: `${prefix}${orderNo}`,
                    sales_order_date: salesOrder?.order_date ?? '',
                    work_schedule_no: r.work_schedule?.schedule_no ?? '',
                    work_schedule_date: r.work_schedule?.schedule_date ?? '',
                    scheduled_quantity: r.work_schedule?.scheduled_qty ?? 0,
                    customer_id: salesOrder?.consignee?.customer?.name ?? '',
                    consignee_id: salesOrder?.consignee?.project_name ?? salesOrder?.consignee?.name ?? '',
                    grade: r.work_schedule?.sales_order_detail?.product?.name ?? '',
                    recipe_code: r.recipe_code || '',
                    rate: r.work_schedule?.sales_order_detail?.rate ?? 0,
                    ordered_quantity: r.work_schedule?.sales_order_detail?.quantity ?? 0,
                    delivery_mode: (r.work_schedule?.sales_order_detail?.delivery_mode ?? r.work_schedule?.delivery_mode ?? '').toUpperCase(),
                    concrete_structure_id: r.work_schedule?.sales_order_detail?.concrete_structure?.name ?? '',
                    plant: r.work_schedule?.plant?.alias_name ?? '',
                };
            });

            setData({
                records: tableData
            });
        })
        .catch((error) => {
            console.log(error, 'getAllDesignMix failed');
            setData({ records: [] });
            const errorData = error.response?.data ?? error.message;
            if (errorData) {
                displayError(errorData, 'Failed to load design mix list');
            }
        })
        .finally(() => setIsLoading(false));
    }, [cookies]);

    const handleSettingsSaved = useCallback((config) => {
        loadDesignMixList(config);
    }, [loadDesignMixList]);

    useEffect(() => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return undefined;
        }
        let cancelled = false;
        bootstrapListViewLoad({
            cookies,
            viewKey: 'design_mix',
            loadFn: loadDesignMixList,
            isCancelled: () => cancelled,
        });
        return () => { cancelled = true; };
    }, [cookies, cookies.myToken, loadDesignMixList, refreshKey]);
    
    return (

        <div id="outer-container"  className="App" > 
            <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
            <div id="page-wrap">
            <ListFormPageHeader
                module="QUALITY CONTROL"
                title="List of Design Mix"
                viewKey="design_mix"
                onSettingsSaved={handleSettingsSaved}
                formLink="/DesignMix"
                addNewLabel="New Design Mix"
                onExportCSV={handleCSVExport}
                onExportPDF={handlePDFExport}
            />
            <ListPageGradientSummary highlightCard={highlightCard} cards={summaryCards} />
            <div className="container item-list-table-container">
                <div className="table-responsive">
                <ReactTableWrapper
                            title='LIST OF DESIGN MIX'
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditDesignMix}
                            onRowDelete={onDeleteDesignMix}
                            onRowPrint={onPrintDesignMix}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                            hideDelete={true}
                            hidePrint={false}
                            right_pinned={right_pinned}
                        />
                </div>
                </div> 


            </div>
</div>

        
    );

}



export default DesignMixTable;